/**
 * TalentMarketInfoBanner.js — Iter-411u
 *
 * A lightweight, dismissible banner that appears on the talent home
 * when the platform CANNOT confidently place the talent in a market
 * from the data we have. Never blocks anything — direct invitations
 * still reach the talent regardless.
 *
 * Contract:
 *   - Renders only when GET /api/talent/markets/status returns
 *     `should_prompt: true`.
 *   - Rate-limits itself: POST /api/talent/markets/dismiss-prompt on
 *     close. Backend enforces a 72h cooldown via
 *     `market_missing_reminders.last_sent_at`.
 *   - Never displays for talent who already have a `primary_market_id`,
 *     nor for talent whose "my market isn't listed" flag is pending
 *     admin review, nor while their AI-suggested market is queued.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, X, AlertCircle } from 'lucide-react';
import api from '../utils/api';
import { Button } from './ui/button';

const DISMISS_COOLDOWN_MS = 72 * 60 * 60 * 1000; // 72h

export default function TalentMarketInfoBanner() {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  const fetchStatus = useCallback(async () => {
    try {
      const r = await api.get('/talent/markets/status');
      setStatus(r.data);
    } catch (_e) {
      // Silent — banner is optional. 401/403 also silent.
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    // Refresh every 5 minutes in case admin resolves the queue.
    const t = setInterval(fetchStatus, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  // Locally suppress after last dismissal for the cooldown window,
  // BEFORE hitting the API — respects the /72h backend contract.
  const recentlyDismissed = (() => {
    if (!status?.last_prompt_at) return false;
    try {
      const t = new Date(status.last_prompt_at).getTime();
      return Date.now() - t < DISMISS_COOLDOWN_MS;
    } catch {
      return false;
    }
  })();

  if (!status || !status.should_prompt || dismissed || recentlyDismissed) {
    return null;
  }

  const handleDismiss = async () => {
    setDismissed(true);
    try {
      await api.post('/talent/markets/dismiss-prompt');
    } catch (_e) { /* non-blocking */ }
  };

  const handleUpdate = () => {
    // Send to the profile info page where the talent can pick or type
    // their market. The MarketPickerModal is embedded there.
    navigate('/talent/profile');
  };

  return (
    <div
      className="mb-4 rounded-lg border border-amber-400/60 bg-amber-50/90 shadow-sm p-4 flex items-start gap-3"
      role="status"
      data-testid="talent-market-info-required-banner"
    >
      <div className="mt-0.5 text-amber-600">
        <MapPin className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-amber-700" />
          <h3
            className="text-sm font-semibold text-amber-900"
            data-testid="talent-market-info-required-title"
          >
            Market Information Required
          </h3>
        </div>
        <p
          className="text-sm text-amber-800 mt-1 leading-relaxed"
          data-testid="talent-market-info-required-message"
        >
          {status.prompt_reason}
        </p>
        <p className="text-xs text-amber-700/80 mt-2 italic">
          Direct invitations from clients still reach you — this only
          affects discovery of nearby open gigs.
        </p>
        <div className="mt-3 flex gap-2 flex-wrap">
          <Button
            size="sm"
            onClick={handleUpdate}
            className="bg-amber-600 hover:bg-amber-700 text-white h-9"
            data-testid="talent-market-info-required-update-btn"
          >
            Update my market
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="text-amber-800 hover:bg-amber-100 h-9"
            data-testid="talent-market-info-required-dismiss-btn"
          >
            Remind me later
          </Button>
        </div>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="text-amber-700 hover:text-amber-900"
        aria-label="Dismiss"
        data-testid="talent-market-info-required-close-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
